import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {Stack, Box, Text, Flex, Link} from '@salesforce/retail-react-app/app/components/shared/ui'

export const SocialGallery = ({
    title,
    titleUrl,
    titleTag = 'h2',
    titleColor = 'black',
    titleFontFamily = 'caslon',
    subCopy,
    subCopyColor = 'black',
    subCopyFontFamily = 'cormorant',
    galleryID,
    siteName,
    productID,
    product,

    ...rest
}) => {
    const productId = productID || product?.id || product?.productId || null
    const [hasItems, setHasItems] = useState(false) // assume false initially
    const componentRef = useRef(null)

    const links = [
        {url: rest?.linkUrl1, text: rest?.linkCopy1, color: rest?.linkColor1 || 'black'},
        {url: rest?.linkUrl2, text: rest?.linkCopy2, color: rest?.linkColor2 || 'black'},
        {url: rest?.linkUrl3, text: rest?.linkCopy3, color: rest?.linkColor3 || 'black'}
    ]

    const isLinksValid = links.some((link) => link.url && link.text)

    useEffect(() => {
        var CRL8_SITENAME = siteName
        const scriptId = 'gallery-script'
        const galleryId = galleryID

        function loadScript() {
            var e = (window.crl8 = window.crl8 || {}),
                n = !1,
                i = []
            ;(e.ready = function (e) {
                n ? e() : i.push(e)
            }),
                (e.pixel =
                    e.pixel ||
                    function () {
                        e.pixel.q.push(arguments)
                    }),
                (e.pixel.q = e.pixel.q || [])
            var t = window.document,
                o = t.createElement('script'),
                c = e.debug || -1 !== t.location.search.indexOf('crl8-debug=true') ? 'js' : 'min.js'
            ;(o.async = !0),
                (o.src =
                    t.location.protocol +
                    '//edge.curalate.com/sites/' +
                    CRL8_SITENAME +
                    '/site/latest/site.' +
                    c),
                (o.id = scriptId),
                (o.onload = function () {
                    ;(n = !0),
                        i.forEach(function (e) {
                            e()
                        })
                })
            var r = t.getElementsByTagName('script')[0]
            r.parentNode.insertBefore(o, r.nextSibling)
        }

        function initializeGallery() {
            if (window.crl8 && window.crl8.ready) {
                window.crl8.ready(() => {
                    window?.crl8?.destroyExperience(galleryId)
                    window.crl8.createExperience(galleryId)
                })
            }
        }

        if (!document.getElementById(scriptId)) {
            loadScript()
        } else {
            initializeGallery()
        }
    }, [galleryID])

    useEffect(() => {
        if (!componentRef.current) return

        // Get the parent element
        const parent = componentRef.current.parentElement
        if (!parent) return
        // observer on the carousel to check if there are any items
        const observer = new MutationObserver((mutations, obs) => {
            const items = parent.querySelectorAll('.crl8-product-carousel-item')
            if (items.length > 0) {
                setHasItems(true)
                obs.disconnect()
            }
        })

        observer.observe(parent, {
            childList: true,
            subtree: true
        })

        return () => observer.disconnect()
    }, [])

    if (!galleryID && !siteName) {
        return null
    }

    return (
        <Stack
            alignItems="center"
            backgroundColor="white"
            margin="0px auto"
            paddingTop={!hasItems ? 0 : {base: '20px', md: '30px'}}
            paddingBottom={!hasItems ? 0 : {base: '20px', md: '30px'}}
            paddingX={!hasItems ? 0 : {base: '20px', md: '30px'}}
            gap={{base: '20px', sm: '5px', md: '20px'}}
            ref={componentRef}
        >
            <Stack textAlign="center" gap="5px" display={hasItems ? 'block' : 'none'}>
                {title && (
                    <Box as={titleUrl ? Link : 'div'} href={titleUrl}>
                        <Text
                            as={titleTag}
                            color={titleColor}
                            fontFamily={titleFontFamily}
                            fontWeight="400"
                            lineHeight="normal"
                            fontSize={{base: '24px', xs: '32px', md: '42px'}}
                            letterSpacing={{base: '0.12px', xs: '0.16px', md: '0.21px'}}
                        >
                            {title}
                        </Text>
                    </Box>
                )}
                {subCopy && (
                    <Box
                        color={subCopyColor}
                        fontFamily={subCopyFontFamily}
                        fontWeight="300"
                        fontSize="16px"
                        lineHeight="25px"
                        letterSpacing="0.085px"
                        sx={{
                            p: {
                                fontFamily: subCopyFontFamily,
                                fontWeight: '300',
                                fontSize: '16px',
                                lineHeight: '25px',
                                letterSpacing: '0.085px'
                            },
                            a: {
                                color: 'blue.700',
                                _hover: {
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '4px'
                                }
                            }
                        }}
                        dangerouslySetInnerHTML={{
                            __html: subCopy
                        }}
                    />
                )}
            </Stack>

            <Box width="100%">
                <Box
                    data-crl8-container-id={galleryID}
                    {...((productId && {'data-crl8-filter': `productId:'${productId}'`}) || {})}
                ></Box>
            </Box>

            {isLinksValid && (
                <Flex
                    gap="30px"
                    rowGap="10px"
                    flexWrap="wrap"
                    justifyContent="center"
                    paddingX="10px"
                    paddingBottom="10px"
                    marginBottom="20px"
                >
                    {links.map((link, index) =>
                        link.url && link.text ? (
                            <Link
                                key={index}
                                href={link.url}
                                color={link.color}
                                variant="underline"
                                fontSize="16px"
                                lineHeight="25px"
                                letterSpacing="0.08px"
                                textUnderlineOffset="4px"
                            >
                                {link.text}
                            </Link>
                        ) : null
                    )}
                </Flex>
            )}
        </Stack>
    )
}

SocialGallery.propTypes = {
    title: PropTypes.string,
    titleUrl: PropTypes.string,
    titleTag: PropTypes.string,
    titleColor: PropTypes.string,
    titleFontFamily: PropTypes.string,
    subCopy: PropTypes.string,
    subCopyColor: PropTypes.string,
    subCopyFontFamily: PropTypes.string,
    galleryID: PropTypes.string,
    siteName: PropTypes.string,
    productID: PropTypes.string,
    product: PropTypes.object
}

export default SocialGallery
